import {Box, PopoverVirtualElement, Popper, Typography} from '@mui/material';
import ArrIconButton from '../../../common/arr-icon-button/ArrIconButton';
import {ReactComponent as DeleteIcon} from '../../../../assets/images/delete.svg';
import SaveOutlinedIcon from '@mui/icons-material/SaveOutlined';
import React, {RefObject, useCallback, useEffect, useState} from 'react';
import {t} from 'i18next';
import './DeleteModal.scss';
import ArrCheckbox from '../../../common/arr-checkbox/ArrCheckbox';
import ArrButton from '../../../common/arr-button/ArrButton';
import ArrSingleSelectDropdown from '../../../common/arr-select-dropdown/ArrSingleSelectDropdown';
import {ISelectItem} from '../../../../shared/types';
import {Close} from '@mui/icons-material';
import {
  useGetDoNotPrompt,
  useGetRedactionTags,
  useGetUserRedactionTags,
  useSaveRedactionTags,
  useSetDoNotPrompt,
} from '../../../../hooks/document/useDocument';
import {SelectedMarkPosition} from '../../../document-view-prizmdoc/hooks/usePrizmDoc';
import {
  AnnotationNodes,
  ISaveRedactionTags,
  IUserRedactionTagsPayload,
  IUserRedactionTagsResponse,
} from '../../../../api/types/document.type';
import {useSelector} from 'react-redux';
import {RootState} from '../../../../redux/store';
import {ToastType} from '../../../../shared/constants';

type DeleteModalProps = {
  anchorElement: HTMLElement | null | Omit<PopoverVirtualElement, 'nodeType'>;
  onClose?: () => void;
  isReduction?: boolean;
  parentRef: RefObject<HTMLDivElement | null>;
  selectedMark: SelectedMarkPosition | null;
  deleteMark: () => void;
  triggerToast: (
    toastType: ToastType | ((prevState: ToastType) => ToastType),
    toastMessage: string
  ) => void;
};
const enDeleteModal = 'docView.deleteModal';

const DeleteModal: React.FC<DeleteModalProps> = ({
  anchorElement,
  onClose,
  isReduction = false,
  parentRef,
  deleteMark,
  selectedMark,
  triggerToast,
}) => {
  const openPopper = Boolean(anchorElement);
  const {
    user: {securityGroupId},
  } = useSelector((state: RootState) => state.authenticatedUser);
  const id = openPopper ? 'doc-view-delete-modal' : undefined;
  // All state defined in component if required move those into parent and use as a props
  const [isDelete, setIsDelete] = useState<boolean>(false);
  const [isCheckedDonotShow, setIsCheckedDonotShow] = useState<boolean>(false);
  const [tag, setTag] = useState<ISelectItem[] | null>([]);
  const [selectedTag, setSelectedTag] = useState<ISelectItem | null>(null);
  const [userRedactionTags, setUserRedactionTags] = useState<
    IUserRedactionTagsResponse[]
  >([]);
  const {data: shouldNotPrompt} = useGetDoNotPrompt();
  const {mutate: setDoNotPrompt} = useSetDoNotPrompt();
  const nodeId = selectedMark?.mark?.getData('ID');
  const activeDocumentObj = useSelector(
    (state: RootState) => state.docView.activeDoc
  );
  const activeDocumentId = Number(activeDocumentObj?.documentId);
  const annotationLayerId = useSelector(
    (state: RootState) => state.docView.docViewConfiguration.annotationLayerId
  );
  const userRedactionPlayload: IUserRedactionTagsPayload = {
    documentID: activeDocumentId,
    nodeID: String(nodeId),
    isLocked: false,
    name: 'test',
    parentID: 1,
    redactionID: 1,
    securityGroupID: securityGroupId,
  };
  const {data: userRedactionTagsData} = useGetUserRedactionTags(
    userRedactionPlayload,
    isReduction
  );
  const {data: redactionTagsData} = useGetRedactionTags(isReduction);
  const {mutate: saveRedactionTags} = useSaveRedactionTags();

  const handleDelete = useCallback(() => {
    if (shouldNotPrompt) {
      deleteMark();
    } else setIsDelete(true);
  }, [shouldNotPrompt, deleteMark]);

  useEffect(() => {
    setIsDelete(false);
    setIsCheckedDonotShow(false);
  }, [anchorElement]);

  const onYes = useCallback(() => {
    if (isCheckedDonotShow) {
      setDoNotPrompt(true);
    }
    deleteMark();
  }, [isCheckedDonotShow, setDoNotPrompt, deleteMark]);

  const handleSaveRedaction = () => {
    const redactions: number[] = [];
    const annotationNodes: AnnotationNodes[] = [];
    redactions.push(Number(selectedTag?.id));
    annotationNodes.push({nodeID: nodeId ?? '', redactionTags: redactions});
    const savePayload: ISaveRedactionTags = {
      annotationLayerId: annotationLayerId ?? '',
      docID: activeDocumentId,
      annoationNodes: annotationNodes,
    };
    saveRedactionTags(savePayload, {
      onSuccess: () => {
        triggerToast(
          ToastType.Success,
          t(`${enDeleteModal}.saveRedactionTagSuccess`)
        );
      },
      onError: () => {
        triggerToast(
          ToastType.Error,
          t(`${enDeleteModal}.errorSaveRedactionTag`)
        );
      },
    });
  };

  useEffect(() => {
    setTag(
      redactionTagsData?.redactionTagDetails.map((tag) => {
        return {
          id: tag.redactionID,
          name: tag.name,
          isDefault: tag.isSelected,
        } as ISelectItem;
      }) ?? []
    );
  }, [redactionTagsData]);

  useEffect(() => {
    setUserRedactionTags(userRedactionTagsData ?? []);
  }, [userRedactionTagsData]);

  useEffect(() => {
    if (!tag?.length) return;

    // Clear previously selected dropdown when switching marks
    setSelectedTag(null);

    // 1. Prefer userRedactionTags selection
    if (userRedactionTags.length) {
      const selectedFromUser = userRedactionTags.find((t) => t.redactionID);
      if (selectedFromUser) {
        const matchingTag = tag.find(
          (item) => item.id === selectedFromUser.redactionID
        );
        if (matchingTag) {
          setSelectedTag(matchingTag);
          return; // done
        }
      }
    }
  }, [tag, userRedactionTags, selectedMark]);

  return (
    <Popper
      id={id}
      open={openPopper}
      anchorEl={anchorElement}
      placement="bottom"
      className="doc-view-delete-modal"
      disablePortal={false} // stays above root
      data-testid="doc-view-delete-modal"
      modifiers={[
        {
          name: 'preventOverflow',
          options: {
            boundary: parentRef.current, // ← ✔ custom boundary
            padding: 8,
          },
        },
        {
          name: 'flip',
          options: {
            boundary: parentRef.current, // ← ✔ limit flips to parent
            fallbackPlacements: ['top', 'right', 'left'],
          },
        },
        {
          name: 'shift',
          options: {
            boundary: parentRef.current, // ← ✔ shift within parent
          },
        },
      ]}
    >
      <Box className={`modal-header-box ${isDelete && 'expanded-modal'}`}>
        {isReduction ? (
          <Box className="modal-header">
            <Box className="default-redaction-tag-dropdown">
              <ArrSingleSelectDropdown
                dropdownLabel={t(`${enDeleteModal}.defaultRedactionTag`)}
                dropdownItems={tag ?? []}
                selectedValues={selectedTag}
                onSelectionChange={setSelectedTag}
                sizeVariant="small"
                disabled={isDelete}
                dataTestId="asd-default-redaction-tag"
                menuPropClassName="glacier-reduction-menu"
              />
            </Box>
            <ArrIconButton
              icon={<DeleteIcon />}
              onClick={handleDelete}
              ariaLabel="delete-button"
              className={`icon-button ${isDelete && 'pressed'}`}
              testId="redaction-delete-btn"
            />
            <ArrIconButton
              onClick={handleSaveRedaction}
              ariaLabel={t('common.save')}
              icon={<SaveOutlinedIcon fontSize="small" />}
              shape="default"
              disabled={isDelete}
              testId="save-btn"
            />
          </Box>
        ) : (
          <ArrIconButton
            icon={<DeleteIcon />}
            onClick={handleDelete}
            ariaLabel="delete-button"
            className={`icon-button ${isDelete && 'pressed'}`}
            testId="delete-btn"
          />
        )}
        <ArrIconButton
          icon={<Close />}
          onClick={onClose}
          ariaLabel="close-button"
          className="icon-button close-button"
          testId="close-btn"
          shape="none"
        />
      </Box>
      {isDelete && (
        <Box className="modal-body">
          <Box>
            <Typography className="typo-text body4_size" data-testid="msg-text">
              {t(`${enDeleteModal}.msgText`)}
            </Typography>
            <Typography
              className="typo-text body4_size"
              data-testid="continue-msg"
            >
              {t(`${enDeleteModal}.continueMSG`)}
            </Typography>
          </Box>
          <Box className="dont-show-again-box">
            <ArrCheckbox
              checked={isCheckedDonotShow}
              onChange={setIsCheckedDonotShow}
              size="medium"
              data-testid="dont-show-checkbox"
              aria-label={t(`${enDeleteModal}.dontShowMsgLabel`)}
            />
            <Typography
              className="typo-text body4_size"
              data-testid="dont-show-checkbox-label"
            >
              {t(`${enDeleteModal}.dontShowMsgLabel`)}
            </Typography>
          </Box>
          <Box className="modal-actions">
            <Box className="button-row">
              <ArrButton
                variant="secondary"
                title={t('common.no')}
                size="small"
                onClick={onClose}
                dataTestId="no-button"
                className="no-button"
                aria-label={t('common.no')}
              >
                {t('common.no')}
              </ArrButton>
              <ArrButton
                variant="primary"
                title={t('common.yes')}
                size="small"
                onClick={onYes}
                dataTestId="yes-button"
                className="yes-button"
                aria-label={t('common.yes')}
              >
                {t('common.yes')}
              </ArrButton>
            </Box>
          </Box>
        </Box>
      )}
    </Popper>
  );
};

export default DeleteModal;
